import React, { useEffect, useState, useMemo, useContext, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faMagic, 
  faSave, 
  faLock, 
  faUnlock,
  faCheckCircle, 
  faExclamationCircle, 
  faTimes, 
  faTasks, 
  faCheck,
  faSpinner,
  faInfoCircle,
  faCopy,
  faEnvelope,
  faEdit
} from "@fortawesome/free-solid-svg-icons";

import Container from "../../components/UI/Container/Container";
import Spinner from "../../components/UI/Spinner/Spinner";
import Alert, { EAlertColor } from "../../components/UI/Alert/Alert";
import Button from "../../components/UI/Button/Button";
import ConfirmDialog from "../../components/UI/ConfirmDialog/ConfirmDialog";
import ConfirmBillingModal from "../../components/ConfirmBillingModal/ConfirmBillingModal";
import ModalContext, { EModalSize } from "../../components/UI/Modal/ModalContext";
import AiOfferModal from "../../components/Offers/AiOfferModal/AiOfferModal";
import { useAuthUser } from "../../hooks/useAuthUser";
import { IAppState, IOfferRequest, IOffer } from "../../interfaces";
import * as actions from "../../store/actions";
import { batchGetOfferRequests } from "../../store/actions/offerRequestActions";
import OfferService from "../../services/OfferService";
import { Routes } from "../../classes/Routes";
import classes from "./CRM.module.scss";

interface IProps extends RouteComponentProps {}

const formatDateOnly = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
  } catch (e) {
    return '-';
  }
};

const CRM: React.FC<IProps> = ({ history }) => {
  const dispatch = useDispatch();
  const authUser = useAuthUser();
  const { setModal, closeModal } = useContext(ModalContext);

  // Redux state for offerRequests
  const { offerRequests, loadingRequests, requestsError } = useSelector((state: IAppState) => ({
    offerRequests: state.offerRequest.offerRequests,
    loadingRequests: state.offerRequest.loading,
    requestsError: state.offerRequest.error
  }));

  // Local state for offers, loading, and UI
  const [offers, setOffers] = useState<IOffer[]>([]);
  const [loadingOffers, setLoadingOffers] = useState<boolean>(true);
  const [offersError, setOffersError] = useState<string | null>(null);
  const [localOfferRequests, setLocalOfferRequests] = useState<IOfferRequest[]>([]);
  const [draftsVisibleLimit, setDraftsVisibleLimit] = useState<number>(30);

  // Drawer state
  const [selectedItem, setSelectedItem] = useState<{
    lead: IOfferRequest;
    offer: IOffer | null;
  } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [savingOffer, setSavingOffer] = useState<boolean>(false);
  const [generatingAi, setGeneratingAi] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  // Form inputs
  const [priceWithVat, setPriceWithVat] = useState<string>("");
  const [priceVatP, setPriceVatP] = useState<string>("25.5");
  const [priceWithoutVat, setPriceWithoutVat] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [offerState, setOfferState] = useState<string>("DRAFT");
  const [billed, setBilled] = useState<boolean>(false);
  const [archived, setArchived] = useState<boolean>(false);
  const [done, setDone] = useState<boolean>(false);
  const [drawerCopySuccess, setDrawerCopySuccess] = useState<boolean>(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoadingOffers(true);
      setOffersError(null);
      dispatch(actions.listOfferRequests({ limit: true }));
      const providerId = authUser?.isServiceProvider && authUser?.organization?.id
        ? authUser.organization.id.toString()
        : undefined;
      const fetchedOffers = await OfferService.getOffers(undefined, providerId);
      setOffers(fetchedOffers || []);
    } catch (err) {
      console.error("Error fetching offers: ", err);
      setOffersError("Virhe ladattaessa tarjouksia palvelimelta.");
    } finally {
      setLoadingOffers(false);
    }
  }, [dispatch, authUser?.organization?.id, authUser?.isServiceProvider]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Prevent background page scrolling when the detailed CRM drawer is open
  useEffect(() => {
    if (drawerOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [drawerOpen]);

  // Keep track of IDs we have already fetched or are currently fetching to prevent infinite/duplicate request loops in CRM
  const fetchedOrLoadingIdsRef = useRef<Set<string>>(new Set());

  // Sync local offer requests list with Redux list by MERGING (never wipe out locally fetched ones)
  useEffect(() => {
    if (Array.isArray(offerRequests)) {
      offerRequests.forEach(req => {
        if (req && req.id) {
          fetchedOrLoadingIdsRef.current.add(req.id.toString());
        }
      });

      setLocalOfferRequests(prev => {
        const merged = [...prev];
        offerRequests.forEach(req => {
          if (req && req.id && !merged.some(r => r.id?.toString() === req.id.toString())) {
            merged.push(req);
          }
        });
        return merged;
      });
    }
  }, [offerRequests]);

  // HX-535: Batch fetch missing offer requests (replaced N+1 individual calls)
  useEffect(() => {
    if (offers.length > 0) {
      const missingIds = offers
        .map(o => o.offerRequestId?.toString())
        .filter((id): id is string => {
          if (!id) return false;
          const alreadyTracked = fetchedOrLoadingIdsRef.current.has(id) || localOfferRequests.some(r => r.id?.toString() === id);
          return !alreadyTracked;
        });

      if (missingIds.length > 0) {
        const uniqueMissingIds = Array.from(new Set(missingIds));
        
        // Mark as loading immediately to prevent duplicate calls
        uniqueMissingIds.forEach(id => fetchedOrLoadingIdsRef.current.add(id));

        // Single batch call instead of N individual GET requests
        batchGetOfferRequests(uniqueMissingIds)
          .then(results => {
            if (results && results.length > 0) {
              setLocalOfferRequests(prev => {
                const merged = [...prev];
                results.forEach(req => {
                  if (req && req.id && !merged.some(r => r.id?.toString() === req.id?.toString())) {
                    merged.push(req);
                  }
                });
                return merged;
              });
            }
          })
          .catch(err => {
            console.warn("Batch fetch missing offer requests failed:", err);
            uniqueMissingIds.forEach(id => fetchedOrLoadingIdsRef.current.delete(id));
          });
      }
    }
  }, [offers, localOfferRequests]);

  // Calculations for prices on input change
  const handlePriceWithVatChange = (val: string) => {
    setPriceWithVat(val);
    const pW = parseFloat(val) || 0;
    const vP = parseFloat(priceVatP) || 25.5;
    if (pW > 0) {
      const pWO = pW / (1 + vP / 100);
      setPriceWithoutVat(pWO.toFixed(2));
      if (offerState === "DRAFT" || !offerState) {
        setOfferState("SENT");
      }
    } else {
      setPriceWithoutVat("");
    }
  };

  const handlePriceVatPChange = (val: string) => {
    setPriceVatP(val);
    const pW = parseFloat(priceWithVat) || 0;
    const vP = parseFloat(val) || 25.5;
    if (pW > 0) {
      const pWO = pW / (1 + vP / 100);
      setPriceWithoutVat(pWO.toFixed(2));
    } else {
      setPriceWithoutVat("");
    }
  };

  // Open details Drawer
  const openDrawer = (lead: IOfferRequest, offer: IOffer | null) => {
    setSelectedItem({ lead, offer });
    setFormError(null);
    setFormSuccess(null);
    
    if (offer) {
      setPriceWithVat(offer.priceWithVat != null ? Number(offer.priceWithVat).toFixed(2) : "");
      setPriceVatP(offer.priceVatP?.toString() || "25.5");
      setPriceWithoutVat(offer.priceWithoutVat != null ? Number(offer.priceWithoutVat).toFixed(2) : "");
      setMessage(offer.message || "");
      setNotes(offer.notes || "");
      setOfferState(offer.state || "DRAFT");
      setBilled(offer.billed || false);
      setArchived(offer.archived || false);
      setDone(offer.done || false);
    } else {
      setPriceWithVat("");
      setPriceVatP("25.5");
      setPriceWithoutVat("");
      setMessage("");
      setNotes("");
      setOfferState("DRAFT");
      setBilled(false);
      setArchived(false);
      setDone(false);
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedItem(null);
  };

  // Create or Update Offer from Drawer
  const handleSaveOffer = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedItem) return;

    try {
      setSavingOffer(true);
      setFormError(null);
      setFormSuccess(null);

      const pW = parseFloat(priceWithVat) || 0;
      const vP = parseFloat(priceVatP) || 25.5;
      const pWO = pW > 0 ? Number((pW / (1 + vP / 100)).toFixed(2)) : 0;
      const pV = pW > 0 ? Number((pW - pWO).toFixed(2)) : 0;

      let nextState = offerState;
      if ((offerState === "DRAFT" || !offerState) && (pW > 0 || pWO > 0)) {
        nextState = "SENT";
        setOfferState("SENT");
      }

      const spId = selectedItem.offer?.serviceProviderId 
        || (authUser?.isServiceProvider && authUser.organization?.id ? authUser.organization.id.toString() : undefined)
        || authUser?.organization?.id?.toString();

      const offerData: Partial<IOffer> = {
        offerRequestId: selectedItem.lead.id,
        serviceProviderId: spId,
        state: nextState,
        priceWithVat: pW > 0 ? pW : undefined,
        priceWithoutVat: pW > 0 ? pWO : undefined,
        priceVatP: vP,
        priceVat: pW > 0 ? pV : undefined,
        message: message || undefined,
        notes: notes || undefined,
        done,
        billed,
        archived
      };

      let saved: IOffer;
      if (selectedItem.offer?.id) {
        saved = await OfferService.updateOffer(selectedItem.offer.id, offerData);
      } else {
        saved = await OfferService.saveOffer(offerData as IOffer);
      }

      setFormSuccess("Tiedot tallennettu onnistuneesti!");
      setSelectedItem(prev => prev ? { ...prev, offer: saved } : null);
      
      // Update local offers state
      setOffers(prev => {
        const idx = prev.findIndex(o => o.id === saved.id);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = saved;
          return updated;
        } else {
          return [...prev, saved];
        }
      });

      setTimeout(() => setFormSuccess(null), 2500);
    } catch (err) {
      console.error("Error saving offer: ", err);
      setFormError("Virhe tietojen tallennuksessa.");
    } finally {
      setSavingOffer(false);
    }
  };

  const handleDrawerCopy = () => {
    if (!message) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(message).then(() => {
        setDrawerCopySuccess(true);
        setTimeout(() => setDrawerCopySuccess(false), 2500);
      }).catch(() => setDrawerCopySuccess(false));
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = message;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setDrawerCopySuccess(true);
        setTimeout(() => setDrawerCopySuccess(false), 2500);
      } catch (e) {
        setDrawerCopySuccess(false);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDrawerMailto = () => {
    const email = selectedItem?.lead?.email;
    if (!email) return;
    const serviceName = selectedItem?.lead?.service?.name || 'Tarjous';
    const city = selectedItem?.lead?.city ? ` - ${selectedItem.lead.city}` : '';
    const subject = `Tarjous: ${serviceName}${city}`;
    const mailtoUrl = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message || '')}`;
    window.location.href = mailtoUrl;
  };

  const handleOpenAiModal = () => {
    if (!selectedItem?.lead) return;
    const pW = parseFloat(priceWithVat) || 0;
    const vP = parseFloat(priceVatP) || 25.5;
    const pWO = pW > 0 ? Number((pW / (1 + vP / 100)).toFixed(2)) : undefined;

    const spId = selectedItem.offer?.serviceProviderId 
      || (authUser?.isServiceProvider && authUser.organization?.id ? authUser.organization.id.toString() : undefined);

    const offerDataForModal: Partial<IOffer> = {
      ...(selectedItem.offer || {}),
      offerRequestId: selectedItem.lead.id,
      serviceProviderId: spId,
      state: offerState,
      priceWithVat: pW > 0 ? pW : undefined,
      priceWithoutVat: pWO,
      priceVatP: vP,
      message: message || undefined,
      notes: notes || undefined
    };

    setModal({
      isOpen: true,
      size: EModalSize.FULL,
      content: (
        <AiOfferModal
          offerRequest={selectedItem.lead}
          initialOffer={offerDataForModal}
          onClose={closeModal}
          onSaved={(savedOffer) => {
            if (savedOffer) {
              if (savedOffer.priceWithVat != null) setPriceWithVat(String(savedOffer.priceWithVat));
              if (savedOffer.priceWithoutVat != null) setPriceWithoutVat(String(savedOffer.priceWithoutVat));
              if (savedOffer.priceVatP != null) setPriceVatP(String(savedOffer.priceVatP));
              if (savedOffer.message != null) setMessage(savedOffer.message);
              if (savedOffer.state != null) setOfferState(savedOffer.state);
              setSelectedItem(prev => prev ? { ...prev, offer: savedOffer } : null);
              setOffers(prev => {
                const idx = prev.findIndex(o => o.id === savedOffer.id);
                if (idx >= 0) {
                  const updated = [...prev];
                  updated[idx] = savedOffer;
                  return updated;
                }
                return [savedOffer, ...prev];
              });
            }
            closeModal();
          }}
          onApplyToOffer={(applied) => {
            if (applied.priceWithVat !== undefined) setPriceWithVat(String(applied.priceWithVat));
            if (applied.priceWithoutVat !== undefined) setPriceWithoutVat(String(applied.priceWithoutVat));
            if (applied.priceVatP !== undefined) setPriceVatP(String(applied.priceVatP));
            if (applied.message !== undefined) setMessage(applied.message);
            closeModal();
          }}
        />
      )
    });
  };

  // AI draft generator
  const handleGenerateAi = async () => {
    if (!selectedItem?.lead?.id) return;
    try {
      setGeneratingAi(true);
      setFormError(null);

      const pW = parseFloat(priceWithVat) || 0;
      const vP = parseFloat(priceVatP) || 25.5;
      const pWO = pW > 0 ? Number((pW / (1 + vP / 100)).toFixed(2)) : 0;
      const pV = pW > 0 ? Number((pW - pWO).toFixed(2)) : 0;

      const spId = selectedItem.offer?.serviceProviderId 
        || (authUser?.isServiceProvider && authUser.organization?.id ? authUser.organization.id.toString() : undefined);

      const offerData: Partial<IOffer> = {
        priceWithVat: pW > 0 ? pW : undefined,
        priceWithoutVat: pW > 0 ? pWO : undefined,
        priceVatP: vP,
        priceVat: pW > 0 ? pV : undefined,
        serviceProviderId: spId
      };

      const draft = await OfferService.generateAiDraft(selectedItem.lead.id, offerData);
      if (draft) {
        setMessage(draft);
        setFormSuccess("Tekoälyluonnos luotu!");
        setTimeout(() => setFormSuccess(null), 2500);
      }
    } catch (err: any) {
      console.error("Error generating AI draft: ", err);
      const serverMsg = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : null) || err?.message;
      setFormError(serverMsg ? `Virhe tekoälytarjouksen generoinnissa: ${serverMsg}` : "Virhe tekoälytarjouksen generoinnissa.");
    } finally {
      setGeneratingAi(false);
    }
  };

  // Buy access for MICRO users
  const triggerBuyAccess = (leadId: string) => {
    const targetLead = localOfferRequests.find(r => r.id?.toString() === leadId.toString());

    const buyModal = (
      <ConfirmDialog
        body={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', padding: '0.5rem 0' }}>
            <div style={{
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '10px',
              padding: '1.25rem',
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start',
              textAlign: 'left'
            }}>
              <div style={{
                color: '#2563eb',
                fontSize: '1.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#dbeafe',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                flexShrink: 0
              }}>
                <FontAwesomeIcon icon={faUnlock} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <strong style={{ color: '#1e3a8a', fontSize: '0.95rem' }}>Avaa tarjouspyynnön täydelliset tiedot</strong>
                <p style={{ margin: 0, color: '#1e40af', fontSize: '0.85rem', lineHeight: '1.5' }}>
                  Saat heti käyttöösi asiakkaan yhteystiedot ja kohteen kaikki perustiedot. Tiedot ovat käytössäsi ilman aikarajoitusta.
                </p>
              </div>
            </div>

            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              padding: '1rem 1.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.6rem',
              textAlign: 'left'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#475569' }}>
                <FontAwesomeIcon icon={faCheck} style={{ color: '#16a34a', fontSize: '0.9rem', flexShrink: 0 }} /> Asiakkaan täydelliset yhteystiedot (nimi, puhelin, sähköposti)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#475569' }}>
                <FontAwesomeIcon icon={faCheck} style={{ color: '#16a34a', fontSize: '0.9rem', flexShrink: 0 }} /> Kohdetiedot karttalinkkeineen (osoite ja rakennustiedot)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#475569' }}>
                <FontAwesomeIcon icon={faCheck} style={{ color: '#16a34a', fontSize: '0.9rem', flexShrink: 0 }} /> Asiakkaan syöttämät lisätiedot ja tarkennukset
              </div>
            </div>

            {targetLead && (
              <div style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '1rem 1.25rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                textAlign: 'left',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                boxSizing: 'border-box',
                width: '100%'
              }}>
                <div style={{ fontWeight: 600, color: '#1e293b' }}>
                  Tarjouspyynnön kilpailutilanne:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', color: '#334155' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span style={{ color: '#64748b', fontSize: '0.75rem', lineHeight: 1 }}>•</span>
                    <span>
                      <strong>Tarjouspyyntö luotu:</strong> {formatDateOnly(targetLead.created)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span style={{ color: '#64748b', fontSize: '0.75rem', lineHeight: 1 }}>•</span>
                    <span>
                      <strong>Tarjouspyyntöä katselleet yritykset:</strong> {targetLead.viewCount ?? 0}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span style={{ color: '#64748b', fontSize: '0.75rem', lineHeight: 1 }}>•</span>
                    <span>
                      <strong>Tarjouksia tehty (HankiX:n kautta):</strong> {targetLead.offerCount ?? 0}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '10px',
              padding: '1rem 1.25rem',
              marginTop: '0.25rem',
              textAlign: 'left'
            }}>
              <span style={{ fontWeight: 600, color: '#92400e', fontSize: '0.95rem' }}>Avaushinta</span>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#b45309', lineHeight: '1' }}>19 €</span>
                <span style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 500 }}>+ alv</span>
              </div>
            </div>

            <div style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: '#0f172a',
              textAlign: 'center',
              marginTop: '0.5rem',
              borderTop: '1px solid #f1f5f9',
              paddingTop: '1rem'
            }}>
              Haluatko lunastaa tämän tarjouspyynnön tiedot?
            </div>
          </div>
        }
        onConfirm={async () => {
          try {
            closeModal();
            setLoadingOffers(true);
            await dispatch(actions.addAccessOfferRequest(leadId));
            dispatch(actions.addAuthUserOfferRequestAccess(leadId));
            await fetchData();
          } catch (err) {
            console.error("Purchase error: ", err);
          } finally {
            setLoadingOffers(false);
          }
        }}
        onCancel={closeModal}
      />
    );

    if (authUser?.organization?.isBillingConfirmed) {
      setModal({
        isOpen: true,
        title: "Vahvista käyttöoikeuden osto",
        size: EModalSize.SMALL,
        content: buyModal
      });
    } else {
      setModal({
        isOpen: true,
        content: (
          <ConfirmBillingModal
            onSuccess={() => {
              setModal({
                isOpen: true,
                title: "Vahvista käyttöoikeuden osto",
                size: EModalSize.SMALL,
                content: buyModal
              });
            }}
          />
        ),
        size: EModalSize.MEDIUM,
        title: "Vahvista laskutusosoite",
      });
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, id: string, type: "lead" | "offer") => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ id, type }));
  };

  const handleDrop = async (e: React.DragEvent, targetState: string, targetDone: boolean = false) => {
    e.preventDefault();
    const dataStr = e.dataTransfer.getData("text/plain");
    if (!dataStr) return;

    try {
      const { id, type } = JSON.parse(dataStr);
      
      let offerToUpdate: IOffer | null = null;
      let targetLeadId = "";

      if (type === "lead") {
        targetLeadId = id;
        // Check if MICRO access is needed
        if (authUser?.isMicro && !authUser?.hasOfferRequestAccess(id)) {
          triggerBuyAccess(id);
          return;
        }
        // Find if we already have an offer for this lead
        const existing = offers.find(o => o.offerRequestId?.toString() === id.toString());
        if (existing) {
          offerToUpdate = existing;
        }
      } else {
        const existing = offers.find(o => o.id === id);
        if (existing) {
          offerToUpdate = existing;
          targetLeadId = existing.offerRequestId?.toString() || "";
        }
      }

      setLoadingOffers(true);

      if (offerToUpdate) {
        // Move existing offer
        const updated = await OfferService.updateOffer(offerToUpdate.id, {
          state: targetState,
          done: targetDone,
          resolvedDate: (targetState === "WON" || targetState === "LOST") ? new Date().toISOString() : undefined
        });
        
        setOffers(prev => prev.map(o => o.id === updated.id ? updated : o));
      } else {
        // Create new offer draft
        const spId = (authUser?.isServiceProvider && authUser.organization?.id ? authUser.organization.id.toString() : undefined)
          || authUser?.organization?.id?.toString();

        const created = await OfferService.saveOffer({
          offerRequestId: targetLeadId,
          serviceProviderId: spId,
          state: targetState as any,
          done: targetDone,
          resolvedDate: (targetState === "WON" || targetState === "LOST") ? new Date().toISOString() : undefined
        } as IOffer);
        
        setOffers(prev => [...prev, created]);
      }
    } catch (err) {
      console.error("Error updating state on drop: ", err);
      setOffersError("Virhe tilan päivityksessä raahauksen jälkeen.");
    } finally {
      setLoadingOffers(false);
    }
  };

  // Grouping and Filtering Pipeline Data
  const pipelineData = useMemo(() => {
    const list = localOfferRequests || [];
    
    // Group offers by lead id
    const offersMap: Record<string, IOffer> = {};
    offers.forEach(o => {
      if (o.offerRequestId) {
        offersMap[o.offerRequestId.toString()] = o;
      }
    });

    const columns: {
      drafts: Array<{ lead: IOfferRequest; offer: IOffer | null }>;
      sent: Array<{ lead: IOfferRequest; offer: IOffer }>;
      won: Array<{ lead: IOfferRequest; offer: IOffer }>;
      lost: Array<{ lead: IOfferRequest; offer: IOffer }>;
      done: Array<{ lead: IOfferRequest; offer: IOffer }>;
    } = {
      drafts: [],
      sent: [],
      won: [],
      lost: [],
      done: []
    };

    const matchedOfferReqIds = new Set<string>();

    list.forEach(lead => {
      if (!lead || !lead.id) return;
      matchedOfferReqIds.add(lead.id.toString());
      const offer = offersMap[lead.id.toString()] || null;
      
      if (!offer) {
        // Active offer request without offer goes to Drafts
        if (!lead.archived && !lead.deleted) {
          columns.drafts.push({ lead, offer: null });
        }
      } else {
        if (offer.deleted || offer.archived) return;

        if (offer.done) {
          columns.done.push({ lead, offer });
        } else {
          if (offer.state === "DRAFT") {
            columns.drafts.push({ lead, offer });
          } else if (offer.state === "SENT") {
            columns.sent.push({ lead, offer });
          } else if (offer.state === "WON") {
            columns.won.push({ lead, offer });
          } else if (offer.state === "LOST") {
            columns.lost.push({ lead, offer });
          }
        }
      }
    });

    // Make sure all provider offers are visible even if their lead is still loading
    offers.forEach(offer => {
      if (!offer || offer.deleted || offer.archived || !offer.offerRequestId) return;
      const orIdStr = offer.offerRequestId.toString();
      if (!matchedOfferReqIds.has(orIdStr)) {
        const placeholderLead: IOfferRequest = {
          id: offer.offerRequestId,
          firstName: "Tarjouspyyntö",
          lastName: `#${offer.offerRequestId}`,
          archived: false,
          deleted: false
        };
        if (offer.done) {
          columns.done.push({ lead: placeholderLead, offer });
        } else if (offer.state === "DRAFT") {
          columns.drafts.push({ lead: placeholderLead, offer });
        } else if (offer.state === "SENT") {
          columns.sent.push({ lead: placeholderLead, offer });
        } else if (offer.state === "WON") {
          columns.won.push({ lead: placeholderLead, offer });
        } else if (offer.state === "LOST") {
          columns.lost.push({ lead: placeholderLead, offer });
        }
      }
    });

    // Sort drafts: newest first (by created timestamp or id descending)
    columns.drafts.sort((a, b) => {
      const dateA = a.offer?.created || a.lead.created || "";
      const dateB = b.offer?.created || b.lead.created || "";
      if (dateA && dateB) {
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      }
      const idA = Number(a.offer?.id || a.lead.id) || 0;
      const idB = Number(b.offer?.id || b.lead.id) || 0;
      return idB - idA;
    });

    // Sort done column: billed = false to the top
    columns.done.sort((a, b) => {
      const aBilled = a.offer.billed ? 1 : 0;
      const bBilled = b.offer.billed ? 1 : 0;
      return aBilled - bBilled; // 0 (unbilled) comes before 1 (billed)
    });

    return columns;
  }, [localOfferRequests, offers]);

  // Aggregate sums (sis. alv & alv 0%) and counts per column
  const aggregates = useMemo(() => {
    const calcSumWithoutVat = (arr: Array<{ offer: IOffer | null }>) => {
      return arr.reduce((acc, curr) => acc + (curr.offer?.priceWithoutVat || 0), 0);
    };
    const calcSumWithVat = (arr: Array<{ offer: IOffer | null }>) => {
      return arr.reduce((acc, curr) => acc + (curr.offer?.priceWithVat || 0), 0);
    };

    return {
      drafts: { 
        count: pipelineData.drafts.length, 
        sumWithoutVat: calcSumWithoutVat(pipelineData.drafts), 
        sumWithVat: calcSumWithVat(pipelineData.drafts) 
      },
      sent: { 
        count: pipelineData.sent.length, 
        sumWithoutVat: calcSumWithoutVat(pipelineData.sent), 
        sumWithVat: calcSumWithVat(pipelineData.sent) 
      },
      won: { 
        count: pipelineData.won.length, 
        sumWithoutVat: calcSumWithoutVat(pipelineData.won), 
        sumWithVat: calcSumWithVat(pipelineData.won) 
      },
      lost: { 
        count: pipelineData.lost.length, 
        sumWithoutVat: calcSumWithoutVat(pipelineData.lost), 
        sumWithVat: calcSumWithVat(pipelineData.lost) 
      },
      done: { 
        count: pipelineData.done.length, 
        sumWithoutVat: calcSumWithoutVat(pipelineData.done), 
        sumWithVat: calcSumWithVat(pipelineData.done) 
      }
    };
  }, [pipelineData]);

  // Rendering Helper for Cards
  const renderCard = (lead: IOfferRequest, offer: IOffer | null) => {
    const isLocked = authUser?.isMicro && !authUser?.hasOfferRequestAccess(lead.id);
    const fullName = `${lead.firstName || ""} ${lead.lastName || ""}`.trim();
    const displayName = isLocked ? `${lead.lastName || "******"}, ${lead.firstName || ""}` : (fullName || `Tarjouspyyntö #${lead.id}`);
    const displayLocation = lead.city || "";
    const serviceName = lead.service?.name || "";
    
    const displayPriceWithVat = offer?.priceWithVat ? `${Number(offer.priceWithVat).toFixed(2)} € sis. alv` : "";
    const displayPriceWithoutVat = offer?.priceWithoutVat ? `${Number(offer.priceWithoutVat).toFixed(2)} € alv 0%` : "";

    return (
      <div 
        key={lead.id}
        draggable={!isLocked}
        onDragStart={(e) => handleDragStart(e, offer?.id || lead.id, offer ? "offer" : "lead")}
        onClick={() => {
          if (isLocked) {
            triggerBuyAccess(lead.id);
          } else {
            openDrawer(lead, offer);
          }
        }}
        className={`${classes.Card} ${isLocked ? classes.CardLocked : ""}`}
      >
        <div className={classes.CardHeader}>
          <span className={classes.ClientName}>{displayName}</span>
          {isLocked && <FontAwesomeIcon icon={faLock} className={classes.LockIcon} />}
        </div>
        <div className={classes.CardDetails}>
          <span className={classes.ServiceBadge}>{serviceName}</span>
          <span className={classes.LocationSpan}>{displayLocation}</span>
        </div>
        <div className={classes.CardFooter} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {displayPriceWithVat && (
              <span className={classes.PriceTag} style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>
                {displayPriceWithVat}
              </span>
            )}
            {displayPriceWithoutVat && (
              <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                {displayPriceWithoutVat}
              </span>
            )}
          </div>
          {offer?.notes && <FontAwesomeIcon icon={faTasks} title="Muistiinpanoja" className={classes.TaskIcon} style={{ marginBottom: '4px' }} />}
        </div>
      </div>
    );
  };

  const loading = loadingRequests || loadingOffers;

  return (
    <Container>
      <div className={classes.PipelineHeader}>
        <div>
          <h2>Myyntiputki (CRM)</h2>
          <p className={classes.Sub}>Hallitse tarjouksiasi, seuraa tilausten laskutustilaa ja dokumentoi asiakasyhteydet.</p>
        </div>
        <div className={classes.Actions}>
          <Button onClick={fetchData} icon={loading ? faSpinner : undefined} disabled={loading} style={{ padding: "6px 12px", fontSize: "0.9rem" }}>
            Päivitä näkymä
          </Button>
        </div>
      </div>

      {(requestsError || offersError) && <Alert>{requestsError || offersError}</Alert>}

      {loadingRequests && offers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "5rem 0" }}><Spinner /></div>
      ) : (
        <div className={classes.Board}>
          
          {/* 1. DRAFTS COLUMN */}
          <div 
            className={classes.Column}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, "DRAFT", false)}
          >
            <div className={classes.ColHeader}>
              <div>
                <h3>Luonnokset</h3>
                <span className={classes.ColMeta}>{aggregates.drafts.count} tarjousta</span>
              </div>
              <span className={classes.SumBadge} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', padding: '4px 10px', borderRadius: '8px', gap: '2px', height: 'auto' }}>
                <span style={{ fontWeight: 700 }}>{aggregates.drafts.sumWithVat.toFixed(2)} € sis. alv</span>
                <span style={{ fontSize: '0.7rem', opacity: 0.8, fontWeight: 500 }}>{aggregates.drafts.sumWithoutVat.toFixed(2)} € alv 0%</span>
              </span>
            </div>
            <div className={classes.CardList}>
              {pipelineData.drafts.slice(0, draftsVisibleLimit).map(item => renderCard(item.lead, item.offer))}
              {pipelineData.drafts.length === 0 && <p className={classes.EmptyText}>Ei luonnoksia.</p>}

              {pipelineData.drafts.length > draftsVisibleLimit && (
                <div className={classes.DraftsLimitFooter}>
                  <div className={classes.DraftsLimitInfo}>
                    Näytetään {draftsVisibleLimit} / {pipelineData.drafts.length} luonnosta
                  </div>
                  <div className={classes.DraftsLimitActions}>
                    <button 
                      type="button" 
                      className={classes.ShowMoreBtn}
                      onClick={() => setDraftsVisibleLimit(prev => prev + 30)}
                    >
                      Lataa lisää (+30)
                    </button>
                    <button 
                      type="button" 
                      className={classes.ShowAllBtn}
                      onClick={() => setDraftsVisibleLimit(pipelineData.drafts.length)}
                    >
                      Kaikki ({pipelineData.drafts.length})
                    </button>
                  </div>
                  <button 
                    type="button" 
                    className={classes.HistoryLinkBtn}
                    onClick={() => history.push(Routes.OFFER_HISTORY)}
                  >
                    Selaa Tarjoushistoriassa →
                  </button>
                </div>
              )}

              {pipelineData.drafts.length > 30 && draftsVisibleLimit >= pipelineData.drafts.length && (
                <div className={classes.DraftsLimitFooter}>
                  <div className={classes.DraftsLimitInfo}>
                    Kaikki {pipelineData.drafts.length} luonnosta näkyvissä
                  </div>
                  <button 
                    type="button" 
                    className={classes.ShowLessBtn}
                    onClick={() => setDraftsVisibleLimit(30)}
                  >
                    Näytä vähemmän (30)
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 2. SENT COLUMN */}
          <div 
            className={classes.Column}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, "SENT", false)}
          >
            <div className={classes.ColHeader}>
              <div>
                <h3>Lähetetyt</h3>
                <span className={classes.ColMeta}>{aggregates.sent.count} tarjousta</span>
              </div>
              <span className={classes.SumBadge} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', padding: '4px 10px', borderRadius: '8px', gap: '2px', height: 'auto' }}>
                <span style={{ fontWeight: 700 }}>{aggregates.sent.sumWithVat.toFixed(2)} € sis. alv</span>
                <span style={{ fontSize: '0.7rem', opacity: 0.8, fontWeight: 500 }}>{aggregates.sent.sumWithoutVat.toFixed(2)} € alv 0%</span>
              </span>
            </div>
            <div className={classes.CardList}>
              {pipelineData.sent.map(item => renderCard(item.lead, item.offer))}
              {pipelineData.sent.length === 0 && <p className={classes.EmptyText}>Ei lähetettyjä.</p>}
            </div>
          </div>

          {/* 3. WON & LOST COLUMN */}
          <div className={classes.ColumnSplit}>
            
            {/* WON BLOCK */}
            <div 
              className={`${classes.ColumnBlock} ${classes.WonBlock}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, "WON", false)}
            >
              <div className={classes.BlockHeader}>
                <div>
                  <h4 style={{ color: "#166534", margin: 0 }}>Voitetut</h4>
                  <span className={classes.ColMeta}>{aggregates.won.count} tarjousta</span>
                </div>
                <span className={classes.SumBadge} style={{ background: "#dcfce7", color: "#166534", display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', padding: '4px 10px', borderRadius: '8px', gap: '2px', height: 'auto' }}>
                  <span style={{ fontWeight: 700 }}>{aggregates.won.sumWithVat.toFixed(2)} € sis. alv</span>
                  <span style={{ fontSize: '0.7rem', opacity: 0.8, fontWeight: 500 }}>{aggregates.won.sumWithoutVat.toFixed(2)} € alv 0%</span>
                </span>
              </div>
              <div className={classes.CardListBlock}>
                {pipelineData.won.map(item => renderCard(item.lead, item.offer))}
                {pipelineData.won.length === 0 && <p className={classes.EmptyText} style={{ color: "#15803d" }}>Raahaa voitettu tarjous tähän.</p>}
              </div>
            </div>

            {/* LOST BLOCK */}
            <div 
              className={`${classes.ColumnBlock} ${classes.LostBlock}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, "LOST", false)}
            >
              <div className={classes.BlockHeader}>
                <div>
                  <h4 style={{ color: "#991b1b", margin: 0 }}>Ei tilannut</h4>
                  <span className={classes.ColMeta}>{aggregates.lost.count} tarjousta</span>
                </div>
                <span className={classes.SumBadge} style={{ background: "#fee2e2", color: "#991b1b", display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', padding: '4px 10px', borderRadius: '8px', gap: '2px', height: 'auto' }}>
                  <span style={{ fontWeight: 700 }}>{aggregates.lost.sumWithVat.toFixed(2)} € sis. alv</span>
                  <span style={{ fontSize: '0.7rem', opacity: 0.8, fontWeight: 500 }}>{aggregates.lost.sumWithoutVat.toFixed(2)} € alv 0%</span>
                </span>
              </div>
              <div className={classes.CardListBlock}>
                {pipelineData.lost.map(item => renderCard(item.lead, item.offer))}
                {pipelineData.lost.length === 0 && <p className={classes.EmptyText} style={{ color: "#b91c1c" }}>Raahaa ei-tilannut tarjous tähän.</p>}
              </div>
            </div>

          </div>

          {/* 4. DONE COLUMN */}
          <div 
            className={classes.Column}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, "WON", true)}
          >
            <div className={classes.ColHeader}>
              <div>
                <h3>Tehty</h3>
                <span className={classes.ColMeta}>{aggregates.done.count} valmista</span>
              </div>
              <span className={classes.SumBadge} style={{ background: "#e2e8f0", display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', padding: '4px 10px', borderRadius: '8px', gap: '2px', height: 'auto' }}>
                <span style={{ fontWeight: 700 }}>{aggregates.done.sumWithVat.toFixed(2)} € sis. alv</span>
                <span style={{ fontSize: '0.7rem', opacity: 0.8, fontWeight: 500 }}>{aggregates.done.sumWithoutVat.toFixed(2)} € alv 0%</span>
              </span>
            </div>
            <div className={classes.CardList}>
              {pipelineData.done.map(item => {
                const isBilled = item.offer.billed;
                return (
                  <div key={item.lead.id} className={classes.DoneCardContainer}>
                    {renderCard(item.lead, item.offer)}
                    <div className={`${classes.StatusStrip} ${isBilled ? classes.Billed : classes.Unbilled}`}>
                      <FontAwesomeIcon 
                        icon={isBilled ? faCheckCircle : faExclamationCircle} 
                        className={classes.StatusIcon}
                      />
                      <span>{isBilled ? "Laskutettu" : "Tehty - odottaa laskutusta"}</span>
                    </div>
                  </div>
                );
              })}
              {pipelineData.done.length === 0 && <p className={classes.EmptyText}>Raahaa tehdyksi merkittävä tähän.</p>}
            </div>
          </div>

        </div>
      )}

      {/* DETAILED CRM DRAWER PANEL */}
      {drawerOpen && selectedItem && (
        <div className={classes.DrawerOverlay} onClick={closeDrawer}>
          <div className={classes.Drawer} onClick={(e) => e.stopPropagation()}>
            <div className={classes.DrawerHeader}>
              <div>
                <h4>Tarjouksen hallinta & dokumentointi</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '0.35rem', flexWrap: 'wrap' }}>
                  <span className={classes.ServiceTitle} style={{ marginTop: 0 }}>{selectedItem.lead.service?.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      document.getElementById("crm-drawer-message-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    style={{
                      fontSize: '0.75rem',
                      color: '#2563eb',
                      background: '#eff6ff',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      border: '1px solid #bfdbfe',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    Viestikenttään ↓
                  </button>
                </div>
              </div>
              <button className={classes.CloseBtn} onClick={closeDrawer}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className={classes.DrawerScroll}>
              
              {/* Notifications */}
              {formError && <Alert>{formError}</Alert>}
              {formSuccess && <Alert color={EAlertColor.SUCCESS}>{formSuccess}</Alert>}

              {/* Customer details */}
              <div className={classes.SectionBox}>
                <h5>Asiakkaan tiedot</h5>
                <table className={classes.DetailTable}>
                  <tbody>
                    <tr>
                      <th>Asiakas:</th>
                      <td>{selectedItem.lead.firstName} {selectedItem.lead.lastName}</td>
                    </tr>
                    <tr>
                      <th>Osoite:</th>
                      <td>{selectedItem.lead.address}, {selectedItem.lead.zip} {selectedItem.lead.city}</td>
                    </tr>
                    <tr>
                      <th>Puhelin:</th>
                      <td>{selectedItem.lead.phone}</td>
                    </tr>
                    <tr>
                      <th>Sähköposti:</th>
                      <td>{selectedItem.lead.email}</td>
                    </tr>
                    {selectedItem.lead.area && (
                      <tr>
                        <th>Pinta-ala:</th>
                        <td>{selectedItem.lead.area} m²</td>
                      </tr>
                    )}
                    {selectedItem.lead.additionalInfo && (
                      <tr>
                        <th>Tarkennukset:</th>
                        <td><p className={classes.LeadInfoText}>{selectedItem.lead.additionalInfo}</p></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* CRM State controls */}
              <div className={classes.SectionBox}>
                <h5>Myynnin tila</h5>
                
                <div className={classes.StateSelectorRow}>
                  {["DRAFT", "SENT", "WON", "LOST"].map(s => {
                    const isSelected = offerState === s && !done;
                    return (
                      <button 
                        key={s}
                        type="button"
                        onClick={() => {
                          setOfferState(s);
                          setDone(false);
                        }}
                        className={`${classes.StateBtn} ${isSelected ? classes.StateBtnActive : ""}`}
                      >
                        {s === "DRAFT" ? "Luonnos" : s === "SENT" ? "Lähetetty" : s === "WON" ? "Voitettu" : "Ei tilannut"}
                      </button>
                    );
                  })}
                </div>

                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem' }}>
                  {offerState === 'SENT' && (
                    <span>
                      <strong style={{ color: '#1d4ed8' }}>Lähetetty:</strong> Tarjous on toimitettu asiakkaalle. <em>(Huom: HankiX ei lähetä sähköpostia puolestasi automaattisesti)</em>
                    </span>
                  )}
                  {offerState === 'DRAFT' && "Luonnos — Tarjousta valmistellaan sisäisesti, ei vielä toimitettu."}
                  {offerState === 'WON' && "Voitettu — Asiakas on hyväksynyt tarjouksen ja toimeksianto sovittu."}
                  {offerState === 'LOST' && "Ei tilannut — Asiakas ei valinnut tarjousta tai tarjouspyyntö on rauennut."}
                </div>

                <div className={classes.SwitchRow} style={{ marginTop: "1rem" }}>
                  <label className={classes.CheckboxLabel}>
                    <input 
                      type="checkbox" 
                      checked={done} 
                      onChange={(e) => {
                        setDone(e.target.checked);
                        if (e.target.checked) setOfferState("WON");
                      }} 
                    />
                    <span>Työ suoritettu loppuun (Tehty)</span>
                  </label>
                </div>

                {done && (
                  <div className={classes.SwitchRow}>
                    <label className={classes.CheckboxLabel}>
                      <input 
                        type="checkbox" 
                        checked={billed} 
                        onChange={(e) => setBilled(e.target.checked)} 
                      />
                      <span>Laskutettu</span>
                    </label>
                  </div>
                )}

                <div className={classes.SwitchRow}>
                  <label className={classes.CheckboxLabel} style={{ color: "#991b1b" }}>
                    <input 
                      type="checkbox" 
                      checked={archived} 
                      onChange={(e) => setArchived(e.target.checked)} 
                    />
                    <span>Arkistoi tarjous (Piilota putkesta)</span>
                  </label>
                </div>
              </div>

              {/* Price form */}
              <div className={classes.SectionBox}>
                <h5>Tarjoushinta</h5>
                <div className={classes.InputGroup}>
                  <div className={classes.InputControl}>
                    <label>Hinta (sis. alv)</label>
                    <input 
                      type="number" 
                      value={priceWithVat}
                      onChange={(e) => handlePriceWithVatChange(e.target.value)}
                      placeholder="0.00" 
                    />
                  </div>
                  <div className={classes.InputControl}>
                    <label>Alv %</label>
                    <input 
                      type="number" 
                      value={priceVatP}
                      onChange={(e) => handlePriceVatPChange(e.target.value)}
                      placeholder="25.5" 
                    />
                  </div>
                </div>
                <div className={classes.InputControl} style={{ marginTop: "0.5rem" }}>
                  <label>Veroton hinta (alv 0%) - Lasketaan automaattisesti</label>
                  <input 
                    type="text" 
                    value={priceWithoutVat}
                    disabled 
                    className={classes.DisabledInput}
                  />
                </div>
              </div>

              {/* AI generator and customer message */}
              <div id="crm-drawer-message-section" className={classes.SectionBox}>
                <div className={classes.MessageHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <h5 style={{ margin: 0 }}>Viesti asiakkaalle (Tarjouksen sisältö)</h5>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Button 
                      onClick={handleGenerateAi} 
                      disabled={generatingAi} 
                      style={{ padding: "4px 8px", fontSize: "0.8rem", background: "#1d4ed8" }}
                    >
                      <FontAwesomeIcon icon={faMagic} style={{ marginRight: "4px" }} />
                      {generatingAi ? "Generoidaan..." : "AI-vastaus"}
                    </Button>
                    <Button
                      onClick={handleOpenAiModal}
                      title="Muokkaa AI-ohjetta ja esikatsele"
                      style={{ padding: "4px 8px", fontSize: "0.8rem", background: "#f1f5f9", color: "#1d4ed8", border: "1px solid #bfdbfe" }}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                  </div>
                </div>

                <div style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  marginTop: '0.5rem',
                  marginBottom: '0.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                    <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#2563eb', marginTop: '2px', flexShrink: 0, fontSize: '0.85rem' }} />
                    <div style={{ fontSize: '0.785rem', color: '#1e3a8a', lineHeight: '1.4' }}>
                      <strong>Huomio: HankiX ei lähetä sähköpostia puolestasi.</strong> Toimita tarjous asiakkaalle leikepöydältä tai oletussähköpostiohjelmassasi.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: '18px' }}>
                    <button
                      type="button"
                      onClick={handleDrawerCopy}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '4px 8px',
                        fontSize: '0.775rem',
                        fontWeight: 500,
                        backgroundColor: drawerCopySuccess ? '#dcfce7' : '#ffffff',
                        color: drawerCopySuccess ? '#15803d' : '#1e40af',
                        border: drawerCopySuccess ? '1px solid #86efac' : '1px solid #93c5fd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <FontAwesomeIcon icon={drawerCopySuccess ? faCheck : faCopy} />
                      {drawerCopySuccess ? 'Kopioitu!' : 'Kopioi leikepöydälle'}
                    </button>

                    <button
                      type="button"
                      onClick={handleDrawerMailto}
                      disabled={!selectedItem?.lead?.email}
                      title={!selectedItem?.lead?.email ? 'Asiakkaalle ei ole määritetty sähköpostiosoitetta' : 'Avaa sähköpostiohjelma'}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '4px 8px',
                        fontSize: '0.775rem',
                        fontWeight: 500,
                        backgroundColor: '#ffffff',
                        color: !selectedItem?.lead?.email ? '#94a3b8' : '#1e40af',
                        border: !selectedItem?.lead?.email ? '1px solid #cbd5e1' : '1px solid #93c5fd',
                        borderRadius: '4px',
                        cursor: !selectedItem?.lead?.email ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <FontAwesomeIcon icon={faEnvelope} />
                      Avaa sähköpostissa (mailto)
                    </button>
                  </div>
                </div>

                <textarea 
                  rows={8}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Kirjoita tarjouksen viesti asiakkaalle tähän..."
                  className={classes.TextareaInput}
                />
              </div>

              {/* Internal Notes CRM documentation */}
              <div className={classes.SectionBox}>
                <h5>Asiakasviestinnän dokumentointi (Sisäiset muistiinpanot)</h5>
                <textarea 
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (!savingOffer) {
                        handleSaveOffer();
                      }
                    }
                  }}
                  placeholder="Tallenna tähän puhelulokit, asiakaskeskustelujen muistiinpanot ja muut sisäiset CRM-tiedot... (Paina Enter tallentaaksesi, Shift+Enter rivinvaihtoon)"
                  className={classes.TextareaInput}
                />
                <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.35rem', display: 'block' }}>
                  Enter tallentaa · Shift+Enter rivinvaihto
                </span>
              </div>

            </div>

            <div className={classes.DrawerFooter}>
              <Button onClick={closeDrawer} style={{ background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }}>
                Sulje
              </Button>
              <Button onClick={() => handleSaveOffer()} disabled={savingOffer} style={{ padding: "8px 16px" }}>
                <FontAwesomeIcon icon={faSave} style={{ marginRight: "6px" }} />
                {savingOffer ? "Tallennetaan..." : "Tallenna muutokset"}
              </Button>
            </div>
          </div>
        </div>
      )}

    </Container>
  );
};

export default CRM;
